import { Card } from "@/components/ui/Card"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import ChannelMenu, { type ScopeNode } from "@/components/ChannelMenu"
import PeriodMenu, { type PeriodPreset } from "./PeriodMenu"
import type { Filters, Grain, Preset, RowKind, Scope } from "../types"

const REVENUE_PRESETS: PeriodPreset<Exclude<Preset, "custom">>[] = [
  { key: "this_month", label: "This month" },
  { key: "last_3_months", label: "Last 3 months" },
  { key: "this_year", label: "This year" },
]

interface Props {
  filters: Filters
  /** Omit all three scope props on the all-markets page, which has no Scope menu. */
  scope?: Scope
  scopeTree?: ScopeNode[]
  hrefForScope?: (slug: string) => string
  onChange: (patch: Partial<Filters>) => void
  /** Extra control groups rendered after Break down by (e.g. the display-currency menu). */
  children?: React.ReactNode
}

export function ToolbarGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <div className="mb-1.5 text-[11px] font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  )
}

const Group = ToolbarGroup

export default function RevenueToolbar({ filters, scope, scopeTree, onChange, hrefForScope, children }: Props) {
  return (
    <Card className="flex flex-wrap items-end gap-6 px-4 py-3">
      {scope && scopeTree && hrefForScope && (
        <Group label="Scope">
          {/* Same 36px control height as the toggle groups, so the row lines up. */}
          <div className="flex h-9 items-center">
            <ChannelMenu
              scopeTree={scopeTree}
              currentNodeId={scope.id}
              currentNodeName={scope.name}
              hrefForChannel={hrefForScope}
              requireLeaf={false}
            />
          </div>
        </Group>
      )}

      <Group label="Period">
        {/* Same 36px control height as the toggle groups, so the row lines up. */}
        <div className="flex h-9 items-center">
          <PeriodMenu value={filters} presets={REVENUE_PRESETS} onChange={onChange} />
        </div>
      </Group>

      <Group label="View by">
        <ToggleGroup type="single" value={filters.grain} onValueChange={(v) => v && onChange({ grain: v as Grain })}>
          <ToggleGroupItem value="month" size="sm">Month</ToggleGroupItem>
          <ToggleGroupItem value="week" size="sm">Week</ToggleGroupItem>
          <ToggleGroupItem value="day" size="sm">Day</ToggleGroupItem>
        </ToggleGroup>
      </Group>

      <Group label="Break down by">
        <ToggleGroup type="single" value={filters.rows} onValueChange={(v) => v && onChange({ rows: v as RowKind })}>
          <ToggleGroupItem value="channels" size="sm">Channels</ToggleGroupItem>
          <ToggleGroupItem value="products" size="sm">Products</ToggleGroupItem>
        </ToggleGroup>
      </Group>

      {children}
    </Card>
  )
}
